/**
 * Radio — React (web) implementation.
 *
 * All styling references the shared token constants in Radio.ts.
 * No raw hex values or pixel literals in this file.
 */

import React from 'react';
import { ensureFocusVisibleStyle } from '../../elements/focusStyle.web';
import { Icon } from '../Icon/Icon.web';
import {
  RADIO_SIZE,
  RADIO_BORDER_WIDTH,
  RADIO_RADIUS,
  RADIO_TICK_ICON,
  RADIO_TICK_SIZE,
  RADIO_TICK_COLOR,
  radioDotColorsFor,
} from './Radio';

export interface WebRadioProps {
  actionId?: string;
  checked?: boolean;
  disabled?: boolean;
  accessibilityLabel: string;
  onChange?: (next: boolean) => void;
  'data-testid'?: string;
}

const RADIO_FOCUS_STYLE_ID = 'galapagos-radio-focus-visible';
const RADIO_FOCUS_SELECTOR = '[data-galapagos-radio="true"]';

export function Radio({
  actionId = 'ui.radio.change',
  checked = false,
  disabled = false,
  accessibilityLabel,
  onChange,
  'data-testid': dataTestId,
}: WebRadioProps) {
  ensureFocusVisibleStyle(RADIO_FOCUS_STYLE_ID, [RADIO_FOCUS_SELECTOR]);

  const dotColors = radioDotColorsFor(checked);
  const style: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: RADIO_SIZE,
    height: RADIO_SIZE,
    borderRadius: RADIO_RADIUS,
    borderWidth: RADIO_BORDER_WIDTH,
    borderStyle: 'solid',
    borderColor: dotColors.border,
    backgroundColor: dotColors.background,
    padding: 0,
    cursor: disabled ? 'not-allowed' : 'pointer',
  };

  return (
    <button
      data-analytics-action={actionId}
      type="button"
      role="radio"
      aria-checked={checked}
      aria-disabled={disabled || undefined}
      aria-label={accessibilityLabel}
      // Stay focusable when disabled (ADR-025): expose aria-disabled and no-op
      // the handler rather than setting the native `disabled` attribute, which
      // would drop the control from keyboard/screen-reader navigation.
      onClick={disabled ? undefined : () => onChange?.(!checked)}
      data-galapagos-radio="true"
      data-testid={dataTestId}
      style={style}
    >
      {checked && (
        <Icon name={RADIO_TICK_ICON} size={RADIO_TICK_SIZE} color={RADIO_TICK_COLOR} aria-hidden />
      )}
    </button>
  );
}
